/*
 * Copyright (c) 2021. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import type { HeatmapValueType } from '@mui/x-charts-premium';
import {
  type APIErrorResponse,
  type BatchableNumber,
  type ComponentId,
  format,
  Granularity,
  getComponent,
  granularityFreqMap,
  type PathKeys,
  type ProjectListEntry,
  type PydanticErrorResponse,
  type RawAPIErrorResponse,
  type ToSfKey,
  transpose,
} from '@poweralpha/common';
import { add, type Duration, endOfMonth, startOfDay, startOfMonth } from 'date-fns';
import { fromZonedTime } from 'date-fns-tz';
import dayjs, { type Dayjs, type OpUnitType, type QUnitType } from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc.js';
import type { HTTPError } from 'ky';
import {
  get,
  isEmpty,
  isEqual,
  toNumber as lodashToNumber,
  mean,
  range,
  round,
  sum,
  upperFirst,
} from 'lodash';
import numeral from 'numeral';
import { type ParseResult, parse } from 'papaparse';
import { toast } from './paToast';
import type { DataLongToWide, FromObjectEntries, ObjectEntries } from './types';
import type { BatchNumberParsed } from './validation-utils';

dayjs.extend(timezone);
dayjs.extend(utc);

// useful due to https://github.com/biomejs/biome/issues/4154#issuecomment-2574623205
export const WHITESPACE = ' ';

export type DateEntryMode = 'startOfMonth' | 'endOfMonth' | 'dateTime';

export function getEndOfMonth(date?: string | Date | null, offset: Duration = {}): Date {
  return startOfDay(endOfMonth(add(date ? new Date(date) : new Date(), offset)));
}

export function getStartOfMonth(date?: string | Date | null, offset: Duration = {}): Date {
  return startOfMonth(startOfDay(add(date ? new Date(date) : new Date(), offset)));
}

export function getStartOfDay(date?: string | Date | null, offset: Duration = {}): Date {
  if (typeof date === 'string') {
    const strDate = date.includes('T') ? date.split('T')[0] : date;
    return add(new Date(strDate), offset);
  }
  return startOfDay(add(date ? new Date(date) : new Date(), offset));
}

export function getEndOfMonthUTC(date?: string | Date | null, offset: Duration = {}): Date {
  return fromZonedTime(getEndOfMonth(date, offset), 'utc');
}

export function getStartOfMonthUTC(date?: string | Date | null, offset: Duration = {}): Date {
  return fromZonedTime(getStartOfMonth(date, offset), 'utc');
}

export function getStartOfDayUTC(date?: string | Date | null, offset: Duration = {}): Date {
  if (typeof date === 'string') {
    const strDate = date.includes('T') ? date.split('T')[0] : date;
    return add(new Date(strDate), offset);
  }
  return fromZonedTime(startOfDay(add(date ? new Date(date) : new Date(), offset)), 'utc');
}

type GetDateReturn<D extends string | Date | null | undefined> = D extends null | undefined
  ? D
  : Date;
export const getDate = <D extends string | Date | null | undefined>(
  date: D,
  mode?: DateEntryMode,
): GetDateReturn<D> => {
  if (date === null || date === undefined) return date as GetDateReturn<D>;

  switch (mode) {
    case 'startOfMonth':
      return getStartOfMonth(date) as GetDateReturn<D>;
    case 'endOfMonth':
      return getEndOfMonth(date) as GetDateReturn<D>;
    case 'dateTime':
      return date as GetDateReturn<D>;
    default:
      // by default, give start of day
      return getStartOfDay(date) as GetDateReturn<D>;
  }
};

export const getUTCDate = <D extends string | Date | null | undefined>(
  date: D,
  mode?: DateEntryMode,
): GetDateReturn<D> => {
  if (date === null || date === undefined) return date as GetDateReturn<D>;

  switch (mode) {
    case 'startOfMonth':
      return getStartOfMonthUTC(date) as GetDateReturn<D>;
    case 'endOfMonth':
      return getEndOfMonthUTC(date) as GetDateReturn<D>;
    case 'dateTime':
      return fromZonedTime(date, 'UTC') as GetDateReturn<D>;
    default:
      // by default, give start of day
      return getStartOfDayUTC(date) as GetDateReturn<D>;
  }
};

export function getDateDifference(
  date1: Date | string,
  date2: Date | string,
  unit: QUnitType | OpUnitType = 'months',
): number {
  return dayjs(date2).utc(false).diff(dayjs(date1).utc(false), unit);
}

export function isDecimal(value: number): boolean {
  return value % 1 !== 0;
}

export function formatDollar(
  value?: number | null,
  precision = 2,
  scale = 1,
  disableMagnitudeChar = false,
): string {
  if (value === undefined || value === null) {
    return '-';
  }

  const precisionDigits = new Array(precision).fill('0').join('');
  const scaledVal = value / scale;
  if (!disableMagnitudeChar && (scaledVal >= 10000 || scaledVal <= -10000)) {
    return `${numeral(scaledVal).format(`($0.${precisionDigits} a)`)} `;
  }
  return `${numeral(value).format(`($0,0.${precisionDigits})`)} `;
}

export function formatMultiplier(value?: number | null, precision = 1): string {
  if (value === undefined || value === null) {
    return '';
  }
  return `${value.toFixed(precision)}x`;
}

export function formatRound(value: number | null | undefined, digits: number, scale = 1): string {
  if (value === undefined || value === null) {
    return '';
  }
  return numeral(value / scale).format(`0,0.[${new Array(digits).fill('0').join('')}]`);
}

export function formatInt(value?: number | null): string {
  if (value === undefined || value === null) {
    return '';
  }
  return `${Number.parseInt(value.toString(), 10).toLocaleString()}`;
}

export function formatCount(value?: number | null): string {
  if (value === undefined || value === null) {
    return '';
  }
  return `#${value}`;
}

export const deepCopy = <T>(list: T): T => JSON.parse(JSON.stringify(list)) as T;

export const boolToString = (bool: boolean | undefined): string => (bool ? 'Yes' : 'No');

export const delay = (ms: number) =>
  new Promise((res) => {
    setTimeout(res, ms);
  });

export const downloadFile = (blob: Blob, fileName: string, ext: string | null): void => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  if (ext === null) {
    a.download = fileName;
  } else {
    a.download = `${fileName}.${ext}`;
  }
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Deferred so engines that resolve the blob URL asynchronously can still
  // start the download before the URL is revoked.
  setTimeout(() => window.URL.revokeObjectURL(url), 1000);
};

export const stringToColor = (string: string): string => {
  let hash = 0;

  for (let i = 0; i < string.length; i += 1) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }

  let color = '#';
  for (let i = 0; i < 3; i += 1) {
    const value = (hash >> (i * 8)) & 0xff;
    color += `00${value.toString(16)}`.slice(-2);
  }

  return color;
};

export const decodeFile = async (
  file: File | null,
  onComplete: (results: ParseResult<Array<string>>) => void,
) => {
  if (!file) return;
  // @ts-expect-error file type not matching
  parse(file, {
    delimiter: '', // auto-detect
    newline: '', // auto-detect
    quoteChar: '"',
    escapeChar: '"',
    header: false,
    transformHeader: undefined,
    dynamicTyping: false,
    preview: 0,
    encoding: '',
    worker: true,
    comments: false,
    complete: (results) => onComplete(results as ParseResult<Array<string>>),
    // biome-ignore lint/suspicious/noConsole: ok here
    error: console.error,
    download: false,
    skipEmptyLines: false,
    delimitersToGuess: [','],
  });
};

interface TmyDate {
  month: number;
  day: number;
  hour: number;
}

const parseTmyDate = (str: string): TmyDate => ({
  month: Number.parseInt(str.slice(5, 7), 10),
  day: Number.parseInt(str.slice(8, 10), 10),
  hour: Number.parseInt(str.slice(11, 13), 10),
});

export const getUtcOffset = () => dayjs().get('hour') - dayjs().utc().get('hour');

export const buildTimestamp = (
  ts: Array<unknown> | undefined,
  start: string | number,
  opts?: { hourlyFrequency?: number; utc?: boolean },
): Array<Date> => {
  if (!ts) return [];

  const frequency = opts?.hourlyFrequency ?? 1;
  if (typeof start === 'string') {
    let noTzString = start;
    if (start.includes('+')) {
      noTzString = start.split('+')[0];
    } else if (start.split('T').at(-1)?.includes('-')) {
      noTzString = `${start.split('T')[0]}T${start.split('T').at(-1)?.split('-')[0]}`;
    }

    const sd = new Date(noTzString);
    return ts.map((_, i) => {
      const baseDate = add(sd, { hours: i * frequency });
      return add(baseDate, { minutes: baseDate.getTimezoneOffset() - sd.getTimezoneOffset() });
    });
  }

  let sd: Dayjs;
  if (opts?.utc) {
    sd = dayjs(`${start}-01-01T00:00:00.000`).utc(true);
  } else {
    sd = dayjs(`${start}-01-01T00:00:00.000`);
  }
  return ts.map((_, i) => sd.add(i * frequency, 'hours').toDate());
};

export const buildTimestampWithGranularity = (
  startTime: Date | undefined,
  endTime: Date | undefined,
  granularity?: Granularity | number,
): Array<Date> => {
  if (!startTime || !endTime || startTime > endTime) return [];
  const freq =
    typeof granularity === 'number'
      ? granularity
      : granularityFreqMap[(granularity ?? Granularity['1h']).toString()];
  const timestamps: Array<Date> = [];
  const currentTimestamp = startTime;

  while (currentTimestamp <= endTime) {
    timestamps.push(new Date(currentTimestamp));
    currentTimestamp.setTime(currentTimestamp.getTime() + 60 * 60 * 1000 * freq); // Add freq in milliseconds
  }

  return timestamps;
};

interface Convert8760to12x24Options {
  scale?: number;
  averageValues?: boolean;
  isUtcTimestamp?: boolean;
  preserveMissingValues?: boolean;
}

export function convert8760to12x24(
  data: Array<number | null> | undefined | null,
  timestamp: Array<string> | Array<Date> | undefined,
  opts: Convert8760to12x24Options & { preserveMissingValues: true },
): Array<Array<number | null>> | undefined;
export function convert8760to12x24(
  data: number[] | undefined | null,
  timestamp: Array<string> | Array<Date> | undefined,
  opts?: Convert8760to12x24Options & { preserveMissingValues?: false },
): Array<Array<number>> | undefined;
export function convert8760to12x24(
  data: Array<number | null> | undefined | null,
  timestamp: Array<string> | Array<Date> | undefined,
  opts?: Convert8760to12x24Options,
): Array<Array<number | null>> | undefined {
  if (!data || !timestamp) {
    return undefined;
  }

  const dataByMonth: Record<string, Record<string, number[]>> = Object.fromEntries(
    range(12).map((m) => [
      m.toString(),
      Object.fromEntries(range(24).map((h) => [h.toString(), []])),
    ]),
  );

  // Build plotly data array with totals for power
  timestamp.slice(0, 8760).forEach((time, i) => {
    let tmyDate: { month: number; day: number; hour: number };
    if (typeof time === 'string') {
      tmyDate = parseTmyDate(time);
    } else if (opts?.isUtcTimestamp) {
      tmyDate = {
        month: time.getUTCMonth() + 1,
        day: time.getUTCDate(),
        hour: time.getUTCHours(),
      };
    } else {
      tmyDate = {
        month: time.getMonth() + 1,
        day: time.getDate(),
        hour: time.getHours(),
      };
    }

    const monthIndex = tmyDate.month - 1;
    const hourIndex = tmyDate.hour;
    const value = data[i];
    if (opts?.preserveMissingValues && value == null) return;
    dataByMonth[monthIndex.toString()]?.[hourIndex.toString()]?.push(value ?? 0);
  });

  // Convert the totals to sums/averages based on number of days in month
  // and then scale to MWh or GWh as appropriate
  const zTotals = Object.entries(dataByMonth).map(([, dataByHour]) =>
    Object.entries(dataByHour).map(([, vals]) => {
      if (opts?.preserveMissingValues && vals.length === 0) return null;
      return (opts?.averageValues ? mean(vals) : sum(vals)) * (opts?.scale ?? 1);
    }),
  );
  return transpose(zTotals);
}

export function prepareHeatmapData(
  values: Array<Array<number>> | undefined,
): Array<HeatmapValueType> {
  return values?.flatMap((row, i) => row.map((val, j) => [j, i, val])) ?? [];
}

export const grabSingleColumnFromCsv = (str: string): number[] => {
  const strSplit = str
    .split('\n')
    .map((s) => s.split(','))
    .slice(1);
  return strSplit.map((row) => Number.parseFloat(row[1] ?? '0'));
};

export const getQuarterIntFromDate = (date: Date): number => {
  if (date.getMonth() <= 2) {
    return 1;
  }
  if (date.getMonth() > 2 && date.getMonth() <= 5) {
    return 2;
  }
  if (date.getMonth() > 5 && date.getMonth() <= 8) {
    return 3;
  }
  return 4;
};

export const getYearsBetweenDates = (
  date1: Date | null | undefined,
  date2: Date | null | undefined,
): number[] => {
  if (!date1 || !date2) return [];

  const years: Array<number> = [];
  let currentYear = new Date(date1).getFullYear();

  while (currentYear <= new Date(date2).getFullYear()) {
    years.push(currentYear);
    currentYear += 1;
  }

  return years;
};

export const getProjectName = (id: string | null, projects: ProjectListEntry[]) => {
  if (!id) return '';
  return getComponent(id, projects)?.name;
};

interface Point {
  x: number;
  y: number;
}

export const lerp = (x: number, p1: Point, p2: Point) => {
  const m = (p2.y - p1.y) / (p2.x - p1.x);
  const b = p1.y - m * p1.x;
  return m * x + b;
};

export interface ErrorResponse {
  error: string;
  message: string;
  statusCode: number;
}

const getFirstApiError = (err: unknown): APIErrorResponse => {
  if (typeof err === 'string') {
    return { error: 'Bad Request', message: err };
  }

  if (Array.isArray(err) && !isEmpty(err)) {
    return getFirstApiError(err[0] ?? '');
  }

  if (err && typeof err === 'object' && 'msg' in err && 'loc' in err && Array.isArray(err.loc)) {
    return {
      error: 'Bad Request',
      message: `${err.msg} (${err.loc.join('.')})`,
    };
  }

  return { error: 'Internal Server Error', message: 'Unknown error' };
};

export const errorAlert = async (
  err: HTTPError | ErrorResponse,
  ignoreError?: string,
): Promise<APIErrorResponse> => {
  // biome-ignore lint/suspicious/noConsole: console ok here
  console.error(err);
  let res: APIErrorResponse;
  let id: string | undefined;
  if ('response' in err) {
    // Global and form-level error handlers can receive the same HTTPError.
    const rawRes = (await err.response.clone().json()) as
      | RawAPIErrorResponse
      | PydanticErrorResponse
      | string;
    const parsedRes = (typeof rawRes === 'string' ? JSON.parse(rawRes) : rawRes) as Partial<
      RawAPIErrorResponse & PydanticErrorResponse
    > | null;
    res = getFirstApiError(parsedRes?.detail ?? parsedRes?.message);
    id = get(rawRes, 'id');
  } else {
    res = err;
    id = get(err, 'id');
  }

  // biome-ignore lint/suspicious/noConsole: console ok here
  console.log(res.message);
  if (ignoreError ? ignoreError !== res.message : true) {
    toast.error(`${res.error ?? 'Error'}: ${res.message}`, { toastId: res.message });
  }

  return { error: res.error, message: res.message, ...(id ? { id } : {}) };
};

export const getEnumValue = <T extends string, TEnumValue extends string>(
  Enum: Record<T, TEnumValue>,
  key: string | null,
): string => {
  if (!key) return '';
  return Object(Enum)[key];
};

export const batchToArray = (batch: BatchNumberParsed | null): BatchableNumber | null => {
  if (typeof batch?.start === 'number' && batch?.end && batch?.step) {
    return [batch.start, batch.end, batch.step];
  }
  if (typeof batch?.start === 'number') {
    return batch.start;
  }
  return null;
};

export function encodeRequestToFormData<T extends object>(
  request: T,
  file: File | null | undefined,
  fileKey = 'file',
) {
  const form = new FormData();
  if (file) {
    form.append(fileKey, file);
  }

  Object.entries(request).forEach(([key, val]) => {
    form.append(key, JSON.stringify(val));
  });

  return form;
}

export function formatComponentAttribute<T>(
  c: T | undefined,
  key: PathKeys<T>,
  opts?: {
    scale?: number;
    noFormat?: boolean;
    precision?: number;
    arrayJoinDelimiter?: string;
    render?: (val: React.ReactNode) => React.ReactNode;
  } | null,
): React.ReactNode {
  const val = get(c, key, null) as React.ReactNode;
  if (opts?.render) {
    return opts.render(val);
  }
  if (opts?.noFormat) {
    return val ?? null;
  }

  if (Array.isArray(val)) {
    return val.join(opts?.arrayJoinDelimiter ?? ',');
  }

  if (typeof val === 'boolean') {
    if (val) {
      return 'Yes';
    }
    if (!val) {
      return 'No';
    }
    return '';
  }
  if (typeof val === 'string' && new Date(val).toString() !== 'Invalid Date') {
    return dayjs(val).utc(false).format('MM/DD/YYYY');
  }
  if (typeof val === 'number') {
    return format(val, opts?.precision ?? 0, opts?.scale);
  }

  return val;
}

export function objectKeys<T extends object>(o: T) {
  return Object.keys(o) as Array<keyof T>;
}

export function getFirstError<T>(
  errors: Partial<Record<keyof T & string, unknown>>,
  ...preferKeys: Array<keyof T & string>
): string | undefined {
  const errorKeys = objectKeys(errors);

  if (errorKeys.length === 0) {
    return undefined;
  }

  let error: unknown;
  for (const preferKey of preferKeys) {
    if (preferKey in errors) {
      error = errors[preferKey];
      break;
    }
  }

  const firstError = error ?? (errorKeys[0] ? errors[errorKeys[0]] : '');
  if (typeof firstError === 'object') {
    return getFirstError(firstError as Record<string, unknown>);
  }
  return firstError as string;
}

export const convertStringToKebabCase = (str: string) => str.toLowerCase().replace(/ /g, '-');

export function fromObjectEntries<T extends Array<[string | number | symbol, unknown]>>(
  e: T,
): FromObjectEntries<T> {
  return Object.fromEntries(e) as FromObjectEntries<T>;
}

export function objectEntries<T extends object>(o: T): ObjectEntries<T> {
  return Object.entries(o) as ObjectEntries<T>;
}

// biome-ignore lint/suspicious/noExplicitAny: generic type
export function reformatDataLongToWide<T extends Partial<Record<keyof T, Array<any> | undefined>>>(
  o: T | undefined,
): Array<DataLongToWide<T>> {
  if (!o) return [];
  const keys = objectKeys(o);
  const firstKey = keys.filter((key) => o[key]?.length)[0];
  return (o[firstKey]?.map((_, i) =>
    Object.fromEntries(
      keys.map((key) => {
        const v = o[key as keyof T];
        return [key, v ? v[i] : null];
      }),
    ),
  ) ?? []) as Array<DataLongToWide<T>>;
}

export function filterUpdateMeta<T extends object>(
  current: T,
  initial: Record<keyof T, unknown>,
): Partial<T> {
  // returns only those props of T that have changed
  return fromObjectEntries(
    objectEntries(current).filter(([key, val]) => !isEqual(val, initial[key])),
  );
}

export function transformRecordToArrayWithIds<T>(
  record: Record<string, T>,
): Array<T & ComponentId> {
  return objectEntries(record).map(([id, data]) => ({
    ...data,
    id,
  }));
}

export const scale = <T extends number | null | undefined>(
  value: T,
  scalingValue: number,
  roundPrecision = 2,
): T => {
  if (value == null || value === undefined) {
    return value;
  }
  return round(value * scalingValue, roundPrecision) as T;
};

export const aggregateData = <T extends Record<string, Array<unknown> | null | undefined>>(
  o: T,
  timestamp: Array<string> | Array<Date>,
  opts?: {
    averageKeys?: Array<keyof T> | 'all';
    outputGranularity?: Granularity | number;
    maxLength?: number;
  },
): T => {
  const date0 = timestamp[0] ? new Date(timestamp[0]) : new Date();
  const granularity = timestamp[1]
    ? (new Date(timestamp[1]).getTime() - date0.getTime()) / 1e3 / 60
    : 60;
  const outputGranularity =
    typeof opts?.outputGranularity === 'number'
      ? opts.outputGranularity * 60
      : (granularityFreqMap[opts?.outputGranularity ?? Granularity['1h']] ?? 1) * 60;
  const step = outputGranularity / granularity;
  if (step === 1) {
    return o;
  }

  const timedelta =
    (new Date(timestamp[timestamp.length - 1] ?? '').getTime() - date0.getTime()) / 1e3 / 60;
  const points = timedelta / granularity;

  const entries = Object.entries(o).filter(([, v]) => typeof v?.[0] === 'number');

  const output = Object.fromEntries(entries.map(([k]) => [k, []])) as Record<string, Array<number>>;
  for (const i of range(0, points + 1, step)) {
    let firstKey = '';
    entries.forEach(([k]) => {
      const agg =
        opts?.averageKeys &&
        ((Array.isArray(opts.averageKeys) && opts?.averageKeys?.includes(k as keyof T)) ||
          opts.averageKeys === 'all')
          ? mean
          : sum;
      output[k]?.push(agg((o[k as keyof T] as Array<number>)?.slice(i, i + step)));
      firstKey = k;
    });
    if (opts?.maxLength && output[firstKey]?.length >= opts.maxLength) {
      break;
    }
  }

  return output as T;
};

export const reindexToJan1Start = <T extends object>(
  data: T,
  timestamp: Array<Date>,
): T & { timestamp: Array<Date> } => {
  const year = (timestamp[0]?.getFullYear() ?? 2020) + 1;
  const startOfYear = new Date(year, 0, 1, 0, 0, 0);
  const findStartIdx = (start: Date) =>
    timestamp.findIndex(
      (t) =>
        t.getFullYear() === start.getFullYear() &&
        t.getMonth() === start.getMonth() &&
        t.getDate() === start.getDate() &&
        t.getHours() === start.getHours(),
    );

  let startOfYearIdx = findStartIdx(startOfYear);

  if (startOfYearIdx === -1) {
    // this is honestly a bit hacky but works when inputting a daily timestamp
    // where the Date switches to daylight savings somewhere throughout the year
    startOfYearIdx = findStartIdx(new Date(year, 0, 1, 23, 0, 0));
  }

  if (startOfYearIdx === -1) {
    // if start of year still cannot be found, it might not be in the data set
    // in which case, there is no reindexing to do
    startOfYearIdx = 0;
  }

  const rotatedTimestamp = [
    ...timestamp.slice(startOfYearIdx, timestamp.length),
    ...timestamp.slice(0, startOfYearIdx),
  ].slice(0, 8760);

  // Build a strictly increasing synthetic Date axis
  // to avoid DST/timezone collisions that create duplicate x values in Plotly.
  const base = new Date(2017, 0, 1, 0, 0, 0).valueOf();
  const stepMs =
    rotatedTimestamp.length > 1
      ? Math.max(1, Math.abs(rotatedTimestamp[1].getTime() - rotatedTimestamp[0].getTime()))
      : 60 * 60 * 1000;

  const syntheticTimestamp = rotatedTimestamp.map((_, i) => new Date(base + i * stepMs));

  return {
    ...fromObjectEntries(
      objectEntries(data).map(([k, v]) => {
        const vals = v as Array<unknown>;
        return [
          k,
          [
            ...(vals?.slice(startOfYearIdx, vals.length) ?? []),
            ...(vals?.slice(0, startOfYearIdx) ?? []),
          ].slice(0, 8760),
        ];
      }),
    ),
    timestamp: syntheticTimestamp,
  } as T & { timestamp: Array<Date> };
};

export const degreesToRadians = (degrees: number) => degrees * (Math.PI / 180);

export const convertStringToDate = (timestamp: string[]): Date[] =>
  timestamp.map((t) => new Date(t));

export const trimString = <S extends string | undefined | null>(
  str: S,
  maxLength: number | undefined,
): S =>
  (maxLength && (str?.length ?? 0) > maxLength ? `${str?.slice(0, maxLength - 3)}...` : str) as S;

export const untransformSfKey = <S extends string>(s: S): ToSfKey<S> =>
  `${s.split('_').map(upperFirst).join('_')}__c` as ToSfKey<S>;

export const calculateFontSize = (
  text: string,
  opts?: {
    minLength?: number;
    maxLength?: number;
    minFontSize?: number;
    maxFontSize?: number;
  },
): number => {
  // Define limits
  const maxFontSize = opts?.minFontSize ?? 14;
  const minFontSize = opts?.maxFontSize ?? 8;

  // Define thresholds for text length
  const minLength = opts?.minLength ?? 17; // Text shorter than this gets max font size
  const maxLength = opts?.maxLength ?? 27; // Text longer than this gets min font size

  // Get text length
  const textLength = text.length;

  // If text is very short, return max font size
  if (textLength <= minLength) {
    return maxFontSize;
  }

  // If text is very long, return min font size
  if (textLength >= maxLength) {
    return minFontSize;
  }

  // Calculate font size using linear interpolation
  const fontSize =
    maxFontSize -
    ((textLength - minLength) / (maxLength - minLength)) * (maxFontSize - minFontSize);

  // Round to nearest integer
  return Math.round(fontSize);
};

export const toNumber = <T extends string | number | null | undefined>(
  str: T,
): T extends undefined ? undefined : T extends null ? null : number => {
  if (str == null || str === undefined) {
    return str as unknown as T extends undefined ? undefined : T extends null ? null : number;
  }
  return lodashToNumber(str) as T extends undefined ? undefined : T extends null ? null : number;
};

export const getUrlDate = (param: string, search = window.location.search) => {
  const date = new URLSearchParams(search).get(param);
  if (date === null) {
    return date;
  }
  return new Date(date);
};
